import { FunctionComponent } from "react";
import { useTranslation } from "react-i18next";
import { faBullhorn, faUserGroup } from "@fortawesome/pro-regular-svg-icons";
import { IconDefinition } from "@fortawesome/fontawesome-svg-core";

import { Icon } from "@/components";
import { classNames } from "@/utils/classNames";

import {
	AnalyticsListContentFilter,
	AnalyticsListContentFilterKey,
} from "../hooks/useAnalyticsListOptions";

export interface ListFilterQuickFiltersProps {
	contentFilter: AnalyticsListContentFilter;
	onToggle: (key: AnalyticsListContentFilterKey) => void;
	/** True while the whole match set is being enumerated for the selection. */
	isApplying: boolean;
}

const QuickFilterRow: FunctionComponent<{
	icon: IconDefinition;
	label: string;
	isActive: boolean;
	isDisabled: boolean;
	testId: string;
	onClick: () => void;
}> = ({ icon, label, isActive, isDisabled, testId, onClick }) => (
	<button
		className={classNames(
			"flex flex-row items-center gap-2 rounded-lg px-2 py-1.5 text-left text-xs font-semibold duration-200",
			isDisabled ? "cursor-not-allowed opacity-60" : "cursor-pointer hover:bg-background-light",
			isActive ? "text-brand-light" : "text-text-meta"
		)}
		data-testid={testId}
		disabled={isDisabled}
		type="button"
		onClick={onClick}
	>
		<span
			className={classNames(
				"flex h-3.5 w-3.5 shrink-0 items-center justify-center rounded border text-[9px] font-bold",
				isActive ? "border-brand-light bg-brand-light text-brand-white" : "border-border-light"
			)}
		>
			{isActive ? "✓" : ""}
		</span>
		<Icon className="w-3 h-3" icon={icon} />
		{label}
	</button>
);

/**
 * The two content quick filters at the top of the List filter.
 *
 * Each one both narrows the options below it and selects everything it matches, so
 * "All lists with campaigns" is one click from a report over exactly those lists — the
 * alternative was ticking them one by one across several pages of the picker.
 */
export const ListFilterQuickFilters: FunctionComponent<ListFilterQuickFiltersProps> = ({
	contentFilter,
	onToggle,
	isApplying,
}) => {
	const { t } = useTranslation();

	return (
		<div className="flex flex-col border-b border-border-light pb-1 mb-1">
			<span className="px-2 py-1 text-[10px] font-semibold uppercase tracking-wide text-text-disabled">
				{t("analytics.filters.quickFilters")}
			</span>

			<QuickFilterRow
				icon={faUserGroup}
				isActive={contentFilter.hasLeads}
				isDisabled={isApplying}
				label={t("analytics.filters.allListsWithLeads")}
				testId="analytics-quick-filter-hasLeads"
				onClick={() => onToggle("hasLeads")}
			/>

			<QuickFilterRow
				icon={faBullhorn}
				isActive={contentFilter.hasCampaigns}
				isDisabled={isApplying}
				label={t("analytics.filters.allListsWithCampaigns")}
				testId="analytics-quick-filter-hasCampaigns"
				onClick={() => onToggle("hasCampaigns")}
			/>
		</div>
	);
};
